import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ciHistoryVerifyQuery, type ChainBreak, type HistoryChain } from '../api/ciHistory'

/**
 * "Verify history" pe fila Changes a unui CI.
 *
 * Serverul recalculeaza lanturile de hash-uri (schimbarile CI-ului si aparatele
 * noi ale firmei lui) si spune, pe fiecare, cate randuri a verificat, cate stau
 * in arhiva, cate sunt ascunse (marcate, nu sterse) si - daca s-a rupt - de la
 * ce rand si de ce. Se cere la clic, nu la deschiderea filei: pe un CI vechi
 * recalcularea citeste tot istoricul lui.
 */

const REASONS: Record<ChainBreak, string> = {
  missing: 'a link of the chain is missing',
  deleted: 'a record was deleted',
  link: 'a record was replaced (the link to the previous one is wrong)',
  hash: 'a record was modified after it was written',
  head: 'the latest records were removed',
}

const count = (n: number) => (n > 0 ? n.toLocaleString('en-US') : '—')

function ChainLine({ chain }: { chain: HistoryChain }) {
  return (
    <tr data-chain={chain.key}>
      <td>{chain.label}</td>
      <td>
        {chain.ok ? (
          <span className="label label-success">intact</span>
        ) : (
          <span className="label label-danger">broken</span>
        )}
      </td>
      <td className="text-right">{chain.checked.toLocaleString('en-US')}</td>
      <td className="text-right rc-muted">{count(chain.archived)}</td>
      <td className="text-right rc-muted">{count(chain.suppressed)}</td>
      <td className="text-right rc-muted">{count(chain.legacy)}</td>
      <td>
        {chain.broken && (
          <span className="text-danger small">
            From record #{chain.broken.seq}
            {chain.broken.id > 0 ? ` (ID ${chain.broken.id})` : ''}: {REASONS[chain.broken.reason]}
          </span>
        )}
      </td>
    </tr>
  )
}

export function HistoryIntegrity({ id }: { id: number }) {
  const [asked, setAsked] = useState(false)
  const result = useQuery(ciHistoryVerifyQuery(id, asked))

  if (!asked) {
    return (
      <button type="button" className="btn btn-default btn-sm rc-verify" onClick={() => setAsked(true)}>
        <i className="fas fa-shield-alt"></i> Verify history
      </button>
    )
  }
  if (result.error) {
    return <p className="text-danger small">The history could not be verified.</p>
  }
  if (!result.data) {
    return <p className="rc-muted small">Recomputing the history…</p>
  }
  const data = result.data
  if (!data.ready) {
    return <p className="rc-muted small">History protection is not installed on this database yet.</p>
  }

  return (
    <div className="rc-integrity" data-ok={data.ok ? '1' : '0'}>
      <p>
        {data.ok ? (
          <span className="label label-success">History intact</span>
        ) : (
          <span className="label label-danger">History altered</span>
        )}{' '}
        {data.protected === false && (
          <span
            className="label label-warning"
            title={`The database triggers that refuse changes and deletions are missing: ${(data.triggers?.missing ?? []).join(', ')}`}
          >
            Not write-protected ({data.triggers?.installed ?? 0}/{data.triggers?.expected ?? 0} guards)
          </span>
        )}{' '}
        <span className="rc-muted small">checked {data.checked_at}</span>
      </p>
      <table className="table table-condensed">
        <thead>
          <tr>
            <th>Chain</th>
            <th style={{ width: 80 }}>State</th>
            <th style={{ width: 90 }} className="text-right">
              Verified
            </th>
            <th style={{ width: 90 }} className="text-right" title="Moved out by retention, kept in the archive">
              Archived
            </th>
            <th style={{ width: 90 }} className="text-right" title="Kept in the history, hidden from the screens">
              Hidden
            </th>
            <th style={{ width: 110 }} className="text-right" title="Written before the chain existed">
              Before chain
            </th>
            <th>Where it broke</th>
          </tr>
        </thead>
        <tbody>
          {data.chains.map((chain) => (
            <ChainLine key={chain.key} chain={chain} />
          ))}
        </tbody>
      </table>
    </div>
  )
}
